import * as React from "react";
import { Link, LinkProps, Route, useRouteMatch } from "react-router-dom";

import { Analytics, VolunteerSearchLink } from "@skv/core";
import { opportunityStore, useThunkDispatch } from "@skv/state";

type HtmlDivAttributes = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

type ErrorLayoutProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

const ErrorLayout: React.FC<ErrorLayoutProps> = ({
  children,
  className,
  ...rest
}) => {
  return (
    <div {...rest} className={className ?? "container py-5"}>
      {children}
    </div>
  );
};

interface DotNetReactRouterStaticContext {
  status?: number;
}

type PropertiesOf<T extends unknown> = {
  [Key in keyof T]: T[Key];
};

type WithErrorLayoutProps<T extends unknown> = {
  wrapperProps?: ErrorLayoutProps;
} & PropertiesOf<T>;

const withErrorLayout = <Props extends unknown>(
  Component: React.ComponentType<
    Pick<WithErrorLayoutProps<Props>, Exclude<keyof Props, "wrapperProps">>
  >,
  statusCode = 200
): typeof WithErrorLayout => {
  const displayName = Component.displayName || "Component";

  const WithErrorLayout = (props: WithErrorLayoutProps<Props>): JSX.Element => {
    const { wrapperProps, ...rest } = props;

    return (
      <>
        <ErrorLayout {...wrapperProps}>
          <Component {...rest} />
        </ErrorLayout>
        <Route
          render={(props) => {
            if (props.staticContext) {
              (props.staticContext as DotNetReactRouterStaticContext).status =
                statusCode;
            }
            return null;
          }}
        />
      </>
    );
  };

  WithErrorLayout.displayName = displayName;

  return WithErrorLayout;
};

interface ErrorContactUsFragmentProps {
  contactUsTo?: string;
}

export const Error404Fragment: React.FC<ErrorContactUsFragmentProps> = ({
  contactUsTo = "/help-and-support/contact-us",
}) => {
  const isExternal = contactUsTo.slice(0, 4) === "http";

  return (
    <div className="row mb-4">
      <div className="d-flex flex-column">
        <h1
          className="font-weight-bold pb-2"
          css={`
            font-size: 3rem;
          `}
        >
          This page is missing
        </h1>
        <p>We can’t find the page you were looking for.</p>
        <ul className="list-unstyled">
          <li>
            {" "}
            <span className="px-3">•</span>Check the web address for typos.
          </li>
          <li>
            {" "}
            <span className="px-3">•</span>Go back to the previous page.
          </li>
        </ul>
        <p>
          If you&apos;re still stuck, let us know by{" "}
          {isExternal ? (
            <a href={contactUsTo}>contacting us</a>
          ) : (
            <Link to={contactUsTo}>contacting us</Link>
          )}
          .
        </p>
      </div>
    </div>
  );
};

export const Error404FragmentWithAnalytics = () => {
  return (
    <>
      <Error404Fragment />
      <Analytics
        data={{
          subSection: "support",
          subSubSection: "support:error",
          subSubSubSection: "support:error+notfound",
          pageTitle: "support:error+notfound",
          pageType: "support:error+notfound",
        }}
      />
    </>
  );
};

export const Error404 = withErrorLayout<ErrorContactUsFragmentProps>(
  Error404FragmentWithAnalytics,
  404
);

export const Error500Fragment = ({
  contactUsTo = "/help-and-support/contact-us",
}: ErrorContactUsFragmentProps): JSX.Element => {
  const isExternal = contactUsTo.slice(0, 4) === "http";

  return (
    <div className="row mb-4">
      <div className="d-flex flex-column">
        <h1
          className="font-weight-bold pb-2"
          css={`
            font-size: 3rem;
          `}
        >
          Something went wrong...{" "}
        </h1>
        <p>We&apos;re not sure what happened, but one of these might help:</p>
        <ul className="list-unstyled">
          <li>
            <span className="px-3">•</span>Go back to the previous page.
          </li>
          <li>
            <span className="px-3">•</span>Reload the page.
          </li>
          <li>
            <span className="px-3">•</span>Use a different browser or device.
          </li>
        </ul>
        <p>
          If you&apos;re still stuck, let us know by{" "}
          {isExternal ? (
            <a href={contactUsTo}>contacting us</a>
          ) : (
            <Link to={contactUsTo}>contacting us</Link>
          )}
          .
        </p>
      </div>
    </div>
  );
};

export const Error500 = withErrorLayout<ErrorContactUsFragmentProps>(
  Error500Fragment,
  500
);

export const OrganisationDetails404Fragment = (): JSX.Element => {
  return (
    <div className="row mb-4">
      <div className="d-flex flex-column">
        <h1
          className="font-weight-bold pb-2"
          css={`
            font-size: 3rem;
          `}
        >
          This organisation isn&#8217;t here.
        </h1>
        <p className="mb-4">Sorry this organisation is no longer available.</p>
        <p>
          You can{" "}
          <VolunteerSearchLink
            to="/volunteering-organisations"
            searchQuery={{}}
            searchType="org"
          >
            search for other organisations
          </VolunteerSearchLink>
          .
        </p>
      </div>
    </div>
  );
};

export const OrganisationDetails404 = withErrorLayout<Record<string, never>>(
  OrganisationDetails404Fragment,
  404
);

export const OpportunityDetails404Fragment = (): JSX.Element => {
  return (
    <div className="row mb-4">
      <div className="d-flex flex-column">
        <h1
          className="font-weight-bold pb-2"
          css={`
            font-size: 3rem;
          `}
        >
          This opportunity has gone.
        </h1>
        <p>Sorry this opportunity is no longer available.</p>
        <p>
          You can{" "}
          <VolunteerSearchLink
            to="/volunteering"
            searchQuery={{}}
            searchType="opp"
          >
            search for current opportunities
          </VolunteerSearchLink>
          .
        </p>
      </div>
    </div>
  );
};

export const OpportunityDetails404 = withErrorLayout(
  OpportunityDetails404Fragment,
  404
);

export const ErrorEoiLimitReachedFragment = (): JSX.Element => {
  return (
    <div className="row mb-4">
      <div className="d-flex flex-column">
        <h1
          className="font-weight-bold pb-2"
          css={`
            font-size: 3rem;
          `}
        >
          You&#8217;ve applied for too many applications.
        </h1>
        <p>
          To help our community organisations manage the volume applications, we
          limit the number of applications you can make in a day.
        </p>
        <p>Please come back tomorrow.</p>
      </div>
    </div>
  );
};

interface ErrorEoiAppliedFragmentProps {
  organisationTo: LinkProps["to"];
  organisationName: React.ReactNode;
}

export const ErrorEoiAppliedFragment: React.FC<
  ErrorEoiAppliedFragmentProps
> = ({ organisationTo, organisationName }) => {
  return (
    <div className="row mb-4">
      <div className="d-flex flex-column">
        <h1
          className="font-weight-bold pb-2"
          css={`
            font-size: 3rem;
          `}
        >
          You&#8217;ve already applied.
        </h1>
        <p>
          Thanks for your application.
          <br />
          To get an update on your application you can contact{" "}
          <Link to={organisationTo}>{organisationName}</Link> directly.
        </p>
      </div>
    </div>
  );
};

export const ErrorEoiApplied = (): JSX.Element => {
  const match = useRouteMatch<{
    id: string;
  }>();

  const { data } = opportunityStore.hooks.useOpportunity(match.params.id);

  const { organisation } = data || {};

  const dispatch = useThunkDispatch();

  React.useEffect(() => {
    return (): void => {
      dispatch({
        type: "OPPORTUNITY:ERROR:RESET",
      });
    };
  }, [dispatch]);

  return (
    <ErrorLayout>
      <ErrorEoiAppliedFragment
        organisationName={organisation.name}
        organisationTo={organisation.url}
      />
    </ErrorLayout>
  );
};

export const ErrorEoiLimitReached = (): JSX.Element => {
  const dispatch = useThunkDispatch();

  React.useEffect(() => {
    return (): void => {
      dispatch({
        type: "OPPORTUNITY:ERROR:RESET",
      });
    };
  }, [dispatch]);

  return (
    <ErrorLayout>
      <ErrorEoiLimitReachedFragment />
    </ErrorLayout>
  );
};
